import React, { useEffect } from 'react';
import styles from './DoubleConfirmationModal.module.css';
import { GlobalButton } from './GlobalButton';
import { GlobalTextarea } from './GlobalTextarea';

interface DoubleConfirmationModalProps {
    isOpen: boolean;
    title: string;
    paragraph: string;
    primaryLabel: string;
    primaryIcon?: string;
    secondaryLabel: string;
    secondaryIcon?: string;
    onPrimary: () => void;
    onSecondary: () => void;
    primaryDestructive?: boolean;
    loading?: boolean;
    showTextarea?: boolean;
    textareaLabel?: string;
    textareaValue?: string;
    onTextareaChange?: (value: string) => void;
    textareaError?: string;
}

const DoubleConfirmationModal: React.FC<DoubleConfirmationModalProps> = ({
    isOpen,
    title,
    paragraph,
    primaryLabel,
    primaryIcon,
    secondaryLabel,
    secondaryIcon,
    onPrimary,
    onSecondary,
    primaryDestructive = false,
    loading = false,
    showTextarea = false,
    textareaLabel = '',
    textareaValue = '',
    onTextareaChange,
    textareaError,
}) => {
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = '';
            };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            {/* Modal */}
            <div className={styles.modal}>

                    {/* Title */}
                    <div className={styles.title}>{title}</div>

                    {/* Paragraph */}
                    <div className={styles.paragraph}>{paragraph}</div>

                    {/* Optional Textarea */}
                    {showTextarea && (
                        <div className={styles.textareaContainer}>
                            <GlobalTextarea
                                label={textareaLabel}
                                value={textareaValue}
                                onChange={(e) => onTextareaChange?.(e.target.value)}
                                error={textareaError}
                            />
                        </div>
                    )}

                    {/* Buttons */}
                    <div className={styles.buttonRow}>

                        {/* Secondary button */}
                        <GlobalButton
                            variant="secondary"
                            onClick={onSecondary}
                            disabled={loading}
                            icon={secondaryIcon ? <img src={secondaryIcon} alt="" width={12} height={12} /> : undefined}
                            iconPosition="left"
                        >
                            {secondaryLabel}
                        </GlobalButton>

                        {/* Primary button */}
                        <GlobalButton
                            variant={primaryDestructive || loading ? 'secondary' : 'primary'}
                            destructive={primaryDestructive}
                            onClick={onPrimary}
                            isLoading={loading}
                            icon={primaryIcon ? <img src={primaryIcon} alt="" width={12} height={12} /> : undefined}
                            iconPosition="left"
                        >
                            {primaryLabel}
                        </GlobalButton>
                    </div>

            </div>
        </div>
    );
};

export default DoubleConfirmationModal;
